/**
 * Web Worker: pinta uma hora do GSMaP fora da thread principal — os bytes
 * viram mm/h, o valor é interpolado numa grade 2× mais fina (com a máscara de
 * sem dado), colorido pela paleta e reprojetado para a grade do GOES pela
 * tabela de vizinho mais próximo. Na thread principal isso custava ~55 ms por
 * troca de hora (442 × 594 pontos → ~1 milhão de pixels), o bastante para o
 * loop engasgar no celular.
 *
 * A tabela de reprojeção (Int32Array de ~1 milhão de posições) é montada uma
 * vez por grade e fica no worker. `pintarHora` também roda na thread
 * principal quando não há Worker (testes).
 */
import { colorir, type Paleta } from '../previsao/camadas';
import { ampliar, type Grade } from '../previsao/interp';
import { construirTabela, reprojetar, type GradeReprojecao } from '../radar/geometria';

export interface PedidoPintura {
  id: number;
  /** Identifica a grade: a tabela de reprojeção é guardada por chave. */
  chave: string;
  grade: Grade;
  repro: GradeReprojecao;
  fator: number;
  /** Byte → mm/h (NaN sem dado). */
  valores: Float32Array;
  paleta: Paleta;
  bytes: Uint8Array;
}

export type RespostaPintura = { id: number; ok: true; image: ImageBitmap } | { id: number; ok: false; error: string };

let tabela: { chave: string; t: Int32Array } | null = null;

function tabelaPara(chave: string, repro: GradeReprojecao, grade: Grade, fator: number): Int32Array {
  if (tabela?.chave !== chave) tabela = { chave, t: construirTabela(repro, grade.nj * fator, grade.ni * fator) };
  return tabela.t;
}

/** Chuva fraca esmaecida: 40% de opacidade em 0,1 mm/h, subindo até a cor cheia em 1 mm/h. */
export const FRACA_ATE = 1;
const FRACA_ALFA = 0.4;

/**
 * Esmaece a chuva fraca (a cor continua a da paleta): o azul-claro de 0,1–1
 * mm/h, opaco, competia com a nuvem branca e transformava as manchas lisas de
 * chuva fraca do produto (a borda da faixa de um satélite) em caixas sobre a
 * mata — o Rainviewer e o Windy também esmaecem a chuva fraca.
 */
export function esmaecerFraca(cores: Uint32Array, valores: ArrayLike<number>, minimo: number): void {
  for (let k = 0; k < cores.length; k++) {
    const v = valores[k];
    const c = cores[k];
    if (c === 0 || !(v < FRACA_ATE)) continue;
    const t = Math.min(1, Math.max(0, (v - minimo) / (FRACA_ATE - minimo)));
    const a = Math.round((c >>> 24) * (FRACA_ALFA + (1 - FRACA_ALFA) * t));
    cores[k] = ((a << 24) | (c & 0xffffff)) >>> 0;
  }
}

/** Os pixels RGBA da hora reprojetada (largura × altura da grade de reprojeção). */
export function pintarHora(p: Omit<PedidoPintura, 'id'>): Uint8ClampedArray<ArrayBuffer> {
  const v = new Float32Array(p.bytes.length);
  for (let k = 0; k < p.bytes.length; k++) v[k] = p.valores[p.bytes[k]];
  const fina = ampliar(v, p.grade, p.fator);
  const cores = colorir(fina, p.paleta);
  esmaecerFraca(cores, fina, p.paleta.paradas[0]?.v ?? 0);
  const saida = new Uint8ClampedArray(p.repro.largura * p.repro.altura * 4);
  reprojetar(cores, tabelaPara(p.chave, p.repro, p.grade, p.fator), new Uint32Array(saida.buffer));
  return saida;
}

// Só instala o manipulador quando roda COMO worker (o módulo também é
// importado pela thread principal, para a reserva).
const ehWorker =
  typeof self !== 'undefined' &&
  typeof (globalThis as { WorkerGlobalScope?: unknown }).WorkerGlobalScope !== 'undefined' &&
  self instanceof (globalThis as unknown as { WorkerGlobalScope: new () => unknown }).WorkerGlobalScope;

if (ehWorker) {
  const escopo = self as unknown as DedicatedWorkerGlobalScope;
  escopo.onmessage = async (e: MessageEvent<PedidoPintura>) => {
    const { id, ...pedido } = e.data;
    try {
      const px = pintarHora(pedido);
      const image = await createImageBitmap(new ImageData(px, pedido.repro.largura, pedido.repro.altura));
      const res: RespostaPintura = { id, ok: true, image };
      escopo.postMessage(res, [image]);
    } catch (err) {
      const res: RespostaPintura = { id, ok: false, error: err instanceof Error ? err.message : String(err) };
      escopo.postMessage(res);
    }
  };
}
